/**
 * Admin → Metrics, Tests and Setup, ported from views/metrics.ejs, tests.ejs and jobs.ejs.
 *
 * Each page is a real URL (the tab bar is link mode), each reads the endpoint `api/v1.js` already exposes for it,
 * and each keeps the EJS page's shape: the same headings, the same columns, the same labels. Where the EJS page
 * has a live-refresh button, this one refetches its query — the numbers come from the same snapshot endpoint.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../api';
import { Card, Cards } from '../../components/Card';
import { Button } from '../../components/Button';
import { InfoTip } from '../../components/InfoTip';
import { Table } from '../../components/Table';
import { nfmt } from '../../format';
import { AdminTabs } from './AdminTabs';
import { lbl } from '../../labels';
import { SideNavShell } from '../../components/SideNav';

type RouteStat = { count: number; p50: number | null; p95: number | null; p99: number | null; avg: number | null; errs4: number; errs5: number };

type MetricsPayload = {
  snapshot: {
    generatedAt: string;
    reqsTotal: number;
    gauges: { loopLagP95: number | null; loopBlockMax: number; rssBytes: number; yahoo: { p95: number | null; total: number; errs: number } };
    routes: Record<string, RouteStat>;
  };
  agg: { ts: string; route: string; count: number; p95: number | null; errs5: number }[];
  reqsToday: number;
  yahooToday: number;
  pending: {
    since?: string;
    flushed?: boolean;
    routes?: { route: string; count: number; p95: number | null; errs5: number }[];
  } | null;
};

const ms = (v: number | null | undefined) => (v == null ? '—' : `${v} ms`);

/**
 * A window's timestamp, in the READER's time.
 *
 * `metrics_agg.ts` is a UTC ISO slice with no zone ("2026-10-11T04:15") — metrics.js writes
 * `new Date().toISOString().slice(0, 16)`. Handed straight to `new Date()`, a string like that is parsed as LOCAL
 * time, so the UTC wall clock would be printed as if it were the reader's: at UTC-7 the 04:15Z window read as
 * "04:15" instead of 9:15 PM the day before. The zone is added before parsing, and the raw UTC rides in the cell's
 * title (views/metrics.ejs does the same).
 */
const fmtWindow = (ts: string | null | undefined): string => {
  if (!ts) return '—';
  const iso = /(Z|[+-]\d\d:?\d\d)$/.test(ts) ? ts : ts + 'Z';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
};

export function AdminMetrics() {
  const q = useQuery<MetricsPayload>({ queryKey: ['admin', 'metrics'], queryFn: () => apiGet<MetricsPayload>('/api/v1/admin/metrics') });
  const d = q.data;
  const routes = d ? Object.entries(d.snapshot.routes).sort((a, b) => (b[1].p95 || 0) - (a[1].p95 || 0)) : [];
  // The open (not yet flushed) window, one row per route — the page shows it above the persisted history.
  const pendingRows = d?.pending?.routes ?? [];

  return (
    <main>
      <AdminTabs active="metrics" />
      {/* views/metrics.ejs wraps the page in a section and puts the snapshot line and the Refresh control on
          one row: the time is a span the page's live-update script rewrites (#m-generated), and Refresh is a
          ghost sm button pinned right. */}
      <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <span className="muted t-small">
          snapshot generated{' '}
          <span id="m-generated" title={d?.snapshot.generatedAt}>
            {d
              ? new Date(d.snapshot.generatedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  second: '2-digit',
                })
              : '…'}
          </span>
        </span>
        <Button
          id="m-refresh"
          variant="ghost"
          size="sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => q.refetch()}
          disabled={q.isFetching}
        >
          {q.isFetching ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      <Cards style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '14px 0' }}>
        <Card label="Event-loop lag p95" valueId="m-lag" value={ms(d?.snapshot.gauges.loopLagP95)} style={{ flex: 1, minWidth: 150 }} />
        <Card label="Max loop block" valueId="m-block" value={d ? `${d.snapshot.gauges.loopBlockMax} ms` : '…'} style={{ flex: 1, minWidth: 150 }} />
        <Card label="RSS" valueId="m-rss" value={d ? `${Math.round(d.snapshot.gauges.rssBytes / 1048576)} MB` : '…'} style={{ flex: 1, minWidth: 150 }} />
        <Card label="Yahoo p95" valueId="m-yahoo" value={ms(d?.snapshot.gauges.yahoo.p95)} style={{ flex: 1, minWidth: 150 }} />
        <Card
          label="Yahoo Requests Today"
          valueId="m-ycalls"
          // The explanation is the card's ⓘ (views/partials/tips/metric-source.ejs), not a `title` on the figure:
          // a native tooltip cannot be opened on a tablet and reads as no explanation at all.
          tip={<InfoTip anchor="card" place="below-left" width={280}>Upstream Yahoo fetches since the last restart, plus the persisted per-interval counts for today.</InfoTip>}
          value={
            d ? (
              <>
                {nfmt(d.snapshot.gauges.yahoo.total)}
                <span className="t-small" style={{ color: 'var(--muted)' }}> / {nfmt(d.yahooToday)}</span>
              </>
            ) : (
              '…'
            )
          }
          style={{ flex: 1, minWidth: 150 }}
        />
        <Card label="Yahoo errors" valueId="m-yerr" value={d ? nfmt(d.snapshot.gauges.yahoo.errs) : '…'} style={{ flex: 1, minWidth: 150 }} />
        <Card
          label="Requests Today"
          valueId="m-reqs"
          tip={<InfoTip anchor="card" place="below-left" width={280}>Live counter since the last restart, plus persisted requests today from the 10-min aggregates.</InfoTip>}
          value={
            d ? (
              <>
                {nfmt(d.snapshot.reqsTotal)}
                <span className="t-small" style={{ color: 'var(--muted)' }}> / {nfmt(d.reqsToday)}</span>
              </>
            ) : (
              '…'
            )
          }
          style={{ flex: 1, minWidth: 150 }}
        />
      </Cards>

      <section className="panel-card" style={{ marginTop: 16 }}>
        <h2>Routes (live)</h2>
        <Table
          expandable={false}
          tableClass="t-body"
          headClass="t-body"
          columns={[
            { label: 'Route' },
            { label: 'Count', num: true },
            { label: 'p50', num: true },
            { label: 'p95', num: true },
            { label: 'p99', num: true },
            { label: 'Avg', num: true },
            { label: '4xx', num: true },
            { label: '5xx', num: true },
          ]}
          rows={routes.map(([route, r]) => ({
            key: route,
            cells: [
              route,
              nfmt(r.count),
              ms(r.p50),
              ms(r.p95),
              ms(r.p99),
              ms(r.avg),
              nfmt(r.errs4 || 0),
              <span key="e" className={r.errs5 ? 'neg' : ''}>
                {nfmt(r.errs5 || 0)}
              </span>,
            ],
          }))}
          empty="No requests observed yet."
        />
      </section>

      <section className="panel-card" style={{ marginTop: 16 }}>
        <h2>History (10-min aggregates, last 24h)</h2>
        {pendingRows.length ? (
          <p className="muted t-small" style={{ margin: '-4px 0 8px' }}>
            The italic {pendingRows.length === 1 ? 'row is' : 'rows are'} the <strong>open window</strong> — traffic
            accumulated since the time shown, written into the history at the next flush
            {d?.pending?.flushed ? '' : ' (nothing has been flushed yet, so this covers everything since the process started)'}.
          </p>
        ) : null}
        <Table
          expandable={false}
          tableClass="t-small"
          columns={[{ label: 'Window' }, { label: 'Route' }, { label: 'Count', num: true }, { label: 'p95', num: true }, { label: '5xx', num: true }]}
          rows={[
            // The open window first, in the page's italic: traffic accumulated since that time, not yet persisted.
            ...pendingRows.map((r) => ({
              key: `pending-${r.route}`,
              cells: [
                <span key="w" style={{ color: 'var(--muted)', fontStyle: 'italic' }}
                  title={`open window: traffic since ${d?.pending?.since} UTC — not yet persisted`}>
                  {`since ${fmtWindow(d?.pending?.since)}`}
                </span>,
                r.route,
                nfmt(r.count),
                ms(r.p95),
                nfmt(r.errs5 || 0),
              ],
            })),
            ...(d?.agg ?? []).map((r, i) => ({
              key: `${r.ts}-${r.route}-${i}`,
              // The window cell is the reader's clock (title: the raw UTC); it was printed as the raw UTC slice,
              // which on this box read four to seven hours ahead of the reader's own clock.
              cells: [
                <span key="w" title={`${r.ts} UTC`}>{fmtWindow(r.ts)}</span>,
                r.route,
                nfmt(r.count),
                ms(r.p95),
                nfmt(r.errs5 || 0),
              ],
            })),
          ]}
          empty="No traffic recorded yet."
        />
      </section>
      </section>
    </main>
  );
}

/**
 * The admin Tests page, reformatted to the storybook's shell: a sticky nav of the areas on the left, one
 * area's suites in the panel.
 *
 * It was one document with every area and all ~940 test names in it, led by a jump row of anchors that
 * duplicated the headings. What the page answers is "what covers the thing I am about to change", which is a
 * question about ONE area at a time — and the answer should be a URL, so `/admin/tests#area-tax` opens Tax.
 *
 * The catalogue is READ FROM SOURCE by services/tests.js: this page lists what exists and never runs anything.
 */

type Suite = { path: string; tests: { name: string }[]; browser?: boolean; description?: string; kindLabel?: string };
type Area = { area: string; slug: string; tests: number; browserTests: number; suites: Suite[] };
export type TestsPayload = {
  suites: Suite[];
  areas: Area[];
  totals: {
    suites: number;
    tests: number;
    browserTests: number;
    uiAreas: number;
    uiAreasWithBrowser: number;
    byKind: Record<string, number>;
  };
  COMMANDS: { all: string; browser: string };
};

/** The entry the address names: `#summary`, `#area-<slug>`, or Summary for anything unrecognised / absent. */
const SUMMARY = 'summary';
function viewFromHash(hash: string, slugs: string[]): string {
  const named = (hash || '').replace(/^#/, '');
  if (!named || named === SUMMARY) return SUMMARY;
  const key = named.startsWith('area-') ? named.slice('area-'.length) : '';
  return slugs.includes(key) ? key : SUMMARY;
}

export function AdminTests() {
  const q = useQuery<TestsPayload>({ queryKey: ['admin', 'tests'], queryFn: () => apiGet<TestsPayload>('/api/v1/admin/tests') });
  const d = q.data;
  const areas = d?.areas ?? [];
  const slugs = areas.map((a) => a.slug);

  const { hash } = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState(() => viewFromHash(hash, slugs));
  // The catalogue arrives after the first render: adopt the address (or Summary) once the slugs exist.
  useEffect(() => { setView(viewFromHash(hash, slugs)); }, [hash, d]);

  const current = areas.find((a) => a.slug === view);
  const shell = useRef<HTMLDivElement | null>(null);

  const go = (key: string) => {
    // flushSync: the panel swaps inside the click, so the DOM already shows the entry the address names by the
    // time the click returns (the storybook's handler does the same, for the same reason).
    flushSync(() => setView(key));
    const href = key === SUMMARY ? `#${SUMMARY}` : `#area-${key}`;
    if (hash !== href) navigate(href);
    // Landing on an entry starts at the shell, not at the scroll offset of the previous — longer — one.
    shell.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
  };

  // A suite can cover more than one area; its description is printed on its FIRST card in the page's order, so
  // the same paragraph is not repeated when the shared suite turns up again under another area.
  const firstCard = new Set<string>();
  const seen = new Set<string>();
  for (const a of areas) {
    for (const s of a.suites) {
      if (!seen.has(s.path)) { seen.add(s.path); firstCard.add(`${a.slug}:${s.path}`); }
    }
  }
  const totalOf = (path: string) => d?.suites.find((x) => x.path === path)?.tests.length ?? 0;

  const items = [
    // Summary leads, and it is why the shell sits at the top of the page: the coverage paragraphs are a page of
    // their own rather than a preamble pushing the nav and the panel down.
    { key: SUMMARY, label: 'Summary', count: d?.totals.tests, title: d ? `${d.totals.suites} suites` : undefined },
    ...areas.map((a) => ({
      key: a.slug,
      label: a.area,
      count: a.tests,
      title: `${a.tests} tests in ${a.suites.length} suite${a.suites.length === 1 ? '' : 's'}`,
    })),
  ];

  return (
    <main>
      <AdminTabs active="tests" />
      <section>
        <div ref={shell} id="tests-shell">
          <SideNavShell
            items={items}
            active={current ? current.slug : view === SUMMARY ? SUMMARY : ''}
            hrefOf={(k) => (k === SUMMARY ? `#${SUMMARY}` : `#area-${k}`)}
            onSelect={go}
            ariaLabel="Test areas"
            wrap
          >
            {view === SUMMARY || !current ? (
              <div id="summary">
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                  <span className="t-h3" style={{ fontWeight: 600 }}>
                    Tests by area
                  </span>
                  <span className="muted t-body">
                    {d ? `${d.totals.suites} suites · ${d.totals.tests} tests · ` : '…'}
                    {d ? (
                      <span style={{ color: 'var(--text)', fontWeight: 600 }}>{d.totals.browserTests} run in a real browser</span>
                    ) : null}
                  </span>
                </div>
                <p className="muted t-body" style={{ margin: '0 0 10px' }}>
                  Browser coverage:{' '}
                  <span style={{ color: 'var(--text)', fontWeight: 600 }}>
                    {d ? `${d.totals.uiAreasWithBrowser} of ${d.totals.uiAreas}` : '…'}
                  </span>{' '}
                  user-facing areas have at least one test that drives a real browser
                  {d && d.totals.uiAreasWithBrowser < d.totals.uiAreas ? ', and the rest do not' : ''}
                  {'.'}
                </p>
                <p className="muted t-body" style={{ margin: '0 0 10px', maxWidth: '100ch' }}>
                  Grouped by the page or area each test covers. The rest do not open a browser at all:{' '}
                  {d ? `${d.totals.byKind.unit || 0} pure logic, ${d.totals.byKind.render || 0} rendering views, ${d.totals.byKind.http || 0} driving the real server over HTTP.` : ''}{' '}
                  Each suite&apos;s label is inferred from what it requires, so a suite that does more than one thing shows
                  the most significant of them. Read from the source, not executed — run them with{' '}
                  <code>{d?.COMMANDS.all ?? ''}</code> and <code>{d?.COMMANDS.browser ?? ''}</code>.
                </p>
              </div>
            ) : (
              <div id={`area-${current.slug}`}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid var(--border)', paddingBottom: 5, marginBottom: 10 }}>
                  <span className="t-h3" style={{ fontWeight: 600 }}>
                    {current.area}
                  </span>
                  <span className="muted t-small">{`${current.tests} test${current.tests === 1 ? '' : 's'}`}</span>
                  {current.browserTests ? (
                    <span className="t-small" style={{ color: 'var(--accent)' }}>
                      {current.browserTests} in a browser
                    </span>
                  ) : !['Core logic', 'API & MCP'].includes(current.area) ? (
                    <span
                      className="t-small"
                      style={{ color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 999, padding: '1px 8px' }}
                    >
                      no browser coverage
                    </span>
                  ) : null}
                </div>

                {current.suites.map((s) => {
                  const showDescription = !!s.description && firstCard.has(`${current.slug}:${s.path}`);
                  return (
                    <div className="panel-card" key={s.path} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                        <code style={{ color: 'var(--text)' }}>{s.path}</code>
                        <span className="muted t-body t-small">
                          {`${s.tests.length} of ${totalOf(s.path)} test${totalOf(s.path) === 1 ? '' : 's'}`}
                        </span>
                        <span
                          className="muted t-small"
                          style={{
                            border: '1px solid var(--border)',
                            borderRadius: 999,
                            padding: '1px 8px',
                            ...(s.browser ? { color: 'var(--accent)', borderColor: 'var(--accent)' } : null),
                          }}
                        >
                          {s.kindLabel}
                        </span>
                      </div>
                      {showDescription ? (
                        <div className="muted t-body" style={{ margin: '6px 0 8px', lineHeight: 1.45, maxWidth: '100ch' }}>
                          {s.description}
                        </div>
                      ) : null}
                      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                        {s.tests.map((t) => (
                          <li className="t-small" key={t.name} style={{ margin: '3px 0', lineHeight: 1.4 }}>
                            {t.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </SideNavShell>
        </div>
      </section>
    </main>
  );
}

type JobRow = {
  id: string;
  group: string;
  title: string;
  what: string;
  install?: string;
  schedule?: string;
  optional?: boolean;
  /** The row's own state word (OK, RUNNING, FAILING, NEEDS FIX, NOT SET UP, UNKNOWN) — not nested under a
   *  `status` object; a row with no state still renders UNKNOWN rather than a dash, because "we could not tell"
   *  is a state and a dash reads as "nothing to report". */
  state?: string | null;
  nextRun?: string | null;
  status?: { state?: string; text?: string } | null;
  detail?: string | null;
  lastRun?: string | null;
  /** Host prerequisites carry the path they check and the rows that need them. */
  path?: string;
  neededBy?: string | null;
};
type JobsPayload = {
  jobs: {
    available: boolean;
    reason?: string;
    hostname: string;
    generatedAt: string;
    /** "11 of 12 required in place · 1 not set up · 1 optional missing" — the page's own summary counts. */
    counts: { inPlace: number; required: number; attention: number; missing: number; optionalMissing: number };
    groups: { services: JobRow[]; timers: JobRow[]; prereqs: JobRow[] };
  };
};

/** The manifest's state words, as views/jobs.ejs colours and spells them. */
const BADGES: Record<string, { cls: string; text: string }> = {
  ok: { cls: 'pos', text: 'OK' },
  running: { cls: 'pos', text: 'RUNNING' },
  failing: { cls: 'neg', text: 'FAILING' },
  overdue: { cls: 'neg', text: 'OVERDUE' },
  needs_fix: { cls: 'neg', text: 'NEEDS FIX' },
  not_set_up: { cls: 'muted', text: 'NOT SET UP' },
  unknown: { cls: 'muted', text: 'UNKNOWN' },
};

function fmtWhen(d: string | Date | null | undefined): string {
  if (d == null || d === '') return '—';
  const when = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(when.getTime())) return '—';
  return when.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function AdminJobs() {
  const q = useQuery<JobsPayload>({ queryKey: ['admin', 'jobs'], queryFn: () => apiGet<JobsPayload>('/api/v1/admin/jobs') });
  const jobs = q.data?.jobs;

  const badgeOf = (r: JobRow) => BADGES[String(r.state ?? '')] || BADGES.unknown;
  const groupTable = (rows: JobRow[] | undefined, columns: { label: ReactNode }[], cellsOf: (r: JobRow) => ReactNode[]) => (
    <Table
      expandable={false}
      // Prose cells wrap: the manifest's "what it does" is a sentence, and a nowrap cell turns it into a
      // horizontal scrollbar instead of a wrapped line.
      wrap
      tableClass=""
      columns={columns}
      rows={(rows ?? []).map((r) => ({ key: r.id, cells: cellsOf(r) }))}
    />
  );

  const titleCell = (r: JobRow, third?: string) => (
    <>
      <strong>{r.title}</strong>
      {r.optional ? ' ' : null}
      {r.optional ? <span className="muted t-small">optional</span> : null}
      <div className="muted t-small">{r.what}</div>
      {third ? <div className="muted t-small">{third}</div> : null}
    </>
  );
  const statusCell = (r: JobRow) => {
    const b = badgeOf(r);
    return (
      <>
        <span className={b.cls}>{b.text}</span>
        {r.detail ? (
          <>
            {' '}
            <span className="muted t-small">{r.detail}</span>
          </>
        ) : null}
      </>
    );
  };

  return (
    <main>
      <AdminTabs active="jobs" />
      {/* views/jobs.ejs's own header row: the host, the counts, when it was read, and a Refresh form. */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', margin: '4px 0 14px' }}>
        <span className="t-lead" style={{ fontWeight: 600 }}>{jobs?.hostname}</span>
        <span className="muted t-body">
          {jobs ? `${jobs.counts.inPlace} of ${jobs.counts.required} required in place` : ''}
          {jobs && jobs.counts.attention ? (
            <>
              {' · '}
              <span className="neg">
                {jobs.counts.attention} need{jobs.counts.attention === 1 ? 's' : ''} attention
              </span>
            </>
          ) : null}
          {jobs && jobs.counts.missing ? ` · ${jobs.counts.missing} not set up` : ''}
          {jobs && jobs.counts.optionalMissing ? ` · ${jobs.counts.optionalMissing} optional missing` : ''}
        </span>
        <span className="muted t-small" style={{ marginLeft: 'auto' }}>read {fmtWhen(jobs?.generatedAt)}</span>
        <form method="get" action="/admin/jobs" style={{ margin: 0 }}>
          <Button variant="ghost" size="sm" className="t-small" type="submit" attrs={{ name: 'refresh', value: '1' }}>
            Refresh
          </Button>
        </form>
      </div>

      {jobs && !jobs.available ? (
        <p className="muted t-body" style={{ margin: '0 0 14px' }}>
          systemd&apos;s user bus is not reachable from this process, so services and timers read UNKNOWN here. The
          file and credential checks below still ran.{jobs.reason ? ` (${jobs.reason})` : ''}
        </p>
      ) : null}

      <section className="panel-card" style={{ marginTop: 0 }}>
        <h2>Services</h2>
        {groupTable(
          jobs?.groups.services,
          [{ label: lbl('Service') }, { label: lbl('Install') }, { label: lbl('Last start') }, { label: lbl('Status') }],
          (r) => [
            titleCell(r),
            <span className="muted t-small">{r.install}</span>,
            <span style={{ whiteSpace: 'nowrap' }}>{fmtWhen(r.lastRun)}</span>,
            statusCell(r),
          ],
        )}
      </section>

      <section className="panel-card">
        <h2>Scheduled jobs</h2>
        {groupTable(
          jobs?.groups.timers,
          [
            { label: lbl('Job') },
            { label: lbl('Schedule') },
            { label: lbl('Last run') },
            { label: lbl('Next run') },
            { label: lbl('Status') },
          ],
          (r) => [
            titleCell(r, r.install),
            <span style={{ whiteSpace: 'nowrap' }}>{r.schedule || '—'}</span>,
            <span style={{ whiteSpace: 'nowrap' }}>{fmtWhen(r.lastRun)}</span>,
            <span style={{ whiteSpace: 'nowrap' }}>{fmtWhen(r.nextRun)}</span>,
            statusCell(r),
          ],
        )}
      </section>

      <section className="panel-card">
        <h2>Host prerequisites</h2>
        {groupTable(
          jobs?.groups.prereqs,
          [{ label: lbl('Prerequisite') }, { label: lbl('Needed by') }, { label: lbl('Status') }],
          (r) => [titleCell(r, r.path), r.neededBy || '—', statusCell(r)],
        )}
      </section>
    </main>
  );
}

